import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import type {OttopilotEventCudLog} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import {
    AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
    AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
    AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__AD_SET_NAME__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
    AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CAMPAIGN__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_CONTENT__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_MEDIUM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_PLATFORM__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__UTM_TERM__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
    AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
    AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
} from "~/domainModels/workspaceSpecific/amaron";
import type {ActivityTimelineResolverContext} from "~/modules/activityTimeline/types/activityTimeline.types";
import {resolveNotesFieldChange} from "~/modules/activityTimeline/utils/activityTimelineNotesUtils";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

const LOG_METADATA_KEYS = new Set([
    "id",
    "event_container_id",
    "eventContainerId",
    "event_id",
    "eventId",
    "template_id",
    "templateId",
    "event_template_id",
    "eventTemplateId",
    "created_at",
    "createdAt",
    "created_by",
    "createdBy",
    "updated_at",
    "updatedAt",
    "updated_by",
    "updatedBy",
    "data",
    "status",
]);

const AMARON_EVENT_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS = new Set(
    [
        AMARON__EVENT__ENQUIRY__UTM_PLATFORM__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__UTM_SOURCE__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__UTM_MEDIUM__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__UTM_CAMPAIGN__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__UTM_TERM__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__UTM_CONTENT__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__GAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__GAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__GBRAID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__GCLID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__FAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__FAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__FBRAID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__FBCLID__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__AD_NAME__COLUMN_ID,
        AMARON__EVENT__ENQUIRY__AD_SET_NAME__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_PLATFORM__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_SOURCE__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_MEDIUM__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_CAMPAIGN__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_TERM__COLUMN_ID,
        AMARON__EVENT__CALLS__UTM_CONTENT__COLUMN_ID,
        AMARON__EVENT__CALLS__GAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__CALLS__GAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__CALLS__GBRAID__COLUMN_ID,
        AMARON__EVENT__CALLS__GCLID__COLUMN_ID,
        AMARON__EVENT__CALLS__FAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__CALLS__FAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__CALLS__FBRAID__COLUMN_ID,
        AMARON__EVENT__CALLS__FBCLID__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_PLATFORM__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_SOURCE__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_MEDIUM__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_CAMPAIGN__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_TERM__COLUMN_ID,
        AMARON__EVENT__SERVICE__UTM_CONTENT__COLUMN_ID,
        AMARON__EVENT__SERVICE__GAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__SERVICE__GAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__SERVICE__GBRAID__COLUMN_ID,
        AMARON__EVENT__SERVICE__GCLID__COLUMN_ID,
        AMARON__EVENT__SERVICE__FAD_SOURCE__COLUMN_ID,
        AMARON__EVENT__SERVICE__FAD_CAMPAIGN_ID__COLUMN_ID,
        AMARON__EVENT__SERVICE__FBRAID__COLUMN_ID,
        AMARON__EVENT__SERVICE__FBCLID__COLUMN_ID,
    ].map(String),
);

export type EventFieldChange = {
    fieldKey: string;
    fieldLabel: string;
    oldValue: unknown;
    newValue: unknown;
};

function valuesEqual(left: unknown, right: unknown): boolean {
    if (left === right) {
        return true;
    }
    if (left === null || left === undefined || left === "") {
        return right === null || right === undefined || right === "";
    }
    if (typeof left === "object" || typeof right === "object") {
        try {
            return JSON.stringify(left) === JSON.stringify(right);
        } catch {
            return false;
        }
    }
    return primitiveComparableString(left) === primitiveComparableString(right);
}

function primitiveComparableString(value: unknown): string {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
        return `${value}`;
    }
    if (typeof value === "symbol") {
        return value.description ?? "";
    }
    return "";
}

function resolveEventTemplate(
    eventTemplateId: Uuid,
    context: ActivityTimelineResolverContext,
): OttopilotEventTemplate | undefined {
    return context.eventTemplates.find((template) => template.id === eventTemplateId);
}

function resolveEventColumnLabel(getAdditionalColumns: CapabilityUdoColumnResolver,
    columnId: string,
    eventTemplateId: Uuid,
    context: ActivityTimelineResolverContext,
): string {
    const template = resolveEventTemplate(eventTemplateId, context);
    const column = getAdditionalColumns(template).find((item) => String(item.id) === columnId);
    if (column !== undefined) {
        return column.displayName;
    }
    return columnId;
}

function shouldSkipEventFieldKey(fieldKey: string, context: ActivityTimelineResolverContext): boolean {
    if (LOG_METADATA_KEYS.has(fieldKey)) {
        return true;
    }
    if (fieldKey === "disposition") {
        return true;
    }
    if (context.workspaceSpecific?.tryAmaronLevelColumns === true) {
        return AMARON_EVENT_ACTIVITY_TIMELINE_SKIP_COLUMN_IDS.has(fieldKey);
    }
    return false;
}

export function extractEventSnapshotFieldChanges(getAdditionalColumns: CapabilityUdoColumnResolver,
    previousSnapshot: OttopilotEventCudLog,
    currentSnapshot: OttopilotEventCudLog,
    context: ActivityTimelineResolverContext,
): Array<EventFieldChange> {
    const changes: Array<EventFieldChange> = [];
    const notesChange = resolveNotesFieldChange(previousSnapshot.notes, currentSnapshot.notes);
    if (notesChange !== null) {
        changes.push({
            fieldKey: "notes",
            fieldLabel: "Notes",
            oldValue: notesChange.oldValue,
            newValue: notesChange.newValue,
        });
    }

    const additionalColumnIds = new Set([
        ...Object.keys(previousSnapshot.additionalColumnValues),
        ...Object.keys(currentSnapshot.additionalColumnValues),
    ]);
    for (const columnId of additionalColumnIds) {
        if (shouldSkipEventFieldKey(columnId, context) === true) {
            continue;
        }
        const oldValue = previousSnapshot.additionalColumnValues[columnId as Uuid];
        const newValue = currentSnapshot.additionalColumnValues[columnId as Uuid];
        if (valuesEqual(oldValue, newValue) === true) {
            continue;
        }
        changes.push({
            fieldKey: columnId,
            fieldLabel: resolveEventColumnLabel(getAdditionalColumns, columnId, currentSnapshot.templateId, context),
            oldValue: oldValue,
            newValue: newValue,
        });
    }

    return changes;
}
